import { Injectable, OnDestroy } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { AlertController, NavController, ToastController } from '@ionic/angular';
import { BehaviorSubject, EMPTY, from, Observable, of, Subscription, throwError, Subject } from 'rxjs';
import { catchError, switchMap, retry, finalize, delay, takeUntil } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { Dialog } from '@capacitor/dialog';
import { Device } from '@capacitor/device';
import { UserService } from './user.service';
import { Swiper } from 'swiper';
import { Browser } from '@capacitor/browser';
import { CapacitorCalendar, CalendarPermissionScope } from '@ebarooni/capacitor-calendar';
import { FileOpener } from '@capacitor-community/file-opener';
import { Filesystem, Directory, DownloadFileResult } from '@capacitor/filesystem';
import { version, build } from '../../../package.json';
import { App } from '@capacitor/app';
import { Share } from '@capacitor/share';
import { environment } from "src/environments/environment";
/*import { StatusBar, Style } from '@capacitor/status-bar';*/
import { FirebaseMessaging, GetTokenOptions } from "@capacitor-firebase/messaging";
import { Router } from '@angular/router';
import { Badge } from '@capawesome/capacitor-badge';
import { e } from '@angular/cdk/scrolling-module.d-ud2XrbF8';

// Configuration constants
const CONFIG = {
  THROTTLE_MS: 2000,
  READ_TIMEOUT: 120000,
  CONNECT_TIMEOUT: 60000,
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000,
  DEFAULT_PRIMARY_COLOR: '#FF6000',
  DEFAULT_PRIMARY_RGB: 'rgb(255,120,0)',
  DEFAULT_SECONDARY_COLOR: '#FFFFFF',
  DEFAULT_SECONDARY_RGB: 'rgb(255,255,255)',
  TOAST_DURATION: 3000,
  SUCCESS_TOAST_DURATION: 2000,
  LOADING_TOAST_DURATION: 1000
} as const;

// Non-loader endpoints
const NON_LOADER_ENDPOINTS = [
  'ionic_postError',
  'ionic_postPushToken',
  'ionic_postUsageStats',
  'ionic_postSwitchDM',
  'ionic_clublogo',
  'ionic_getMembership',
  'ionic_postNotificationSettings',
  'ionic_sendNotifications',
  'ionic_userHelpSearch',
  'ionic_getTrainingGroupRegister',
  'ionic_getMessageBoard',
  'ionic_meetSearch',
  'ionic_raceSearch',
  'ionic_eventSearch',
  'ionic_getEventCalendarHistory',
  'ionic_memberSearch',
  'ionic_getTrainingLocations',
  'ionic_getHighlightEventorOffer',
  'ionic_getAppTabValues',
  'ionic_modalViewed',
  'ionic_postAppleActivity',
  'ionic_getTrainingGroupDetails_V2',
  'ionic_postPageViewed',
  'ionic_getLatestGroupMsg',
  'ionic_postModalViewed',
] as const;

// Type definitions
interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
  logout?: boolean;
  jwttoken?: string;
}

interface RequestOptions {
  url: string;
  headers: Record<string, string>;
  data?: any;
  params?: any;
  readTimeout: number;
  connectTimeout: number;
}

interface ErrorData {
  error?: string;
  status: number;
  data?: {
    message?: string;
  };
  message?: string;
  code?: string;
}

interface DeviceInfo {
  platform: string;
  model: string;
  operatingSystem: string;
}

interface AppInfo {
  version: string;
  build: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalService implements OnDestroy {
  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();
  
  // Add this property to track if maintenance alert is already shown
  private maintenanceAlertShown = false;
  
  // Track recent requests for throttling
  private recentRequests = new Map<string, number>();

  // State and config
  public messageBoardData = new BehaviorSubject<Array<any>>([]);
  public tabValues = new BehaviorSubject<Array<any>>([]);
  public deviceInfo: DeviceInfo | null = null;
  public cMCVersion = version;
  public apiURL: string = environment.apiURL;
  public siteUrl: string = environment.siteUrl;
  public stripeKey: string = environment.stripeKey;
  public mapsApiKey: string = environment.googleMapsApiKey;
  public appName: string = environment.appName;
  public merchantName: string = "Arden:Twenty";
  public primary_color: string = CONFIG.DEFAULT_PRIMARY_RGB;
  public primary_color_hex: string = CONFIG.DEFAULT_PRIMARY_COLOR;
  public statusBarText: string = "DARK";
  public clubName: string = '';
  public pushPermission = new BehaviorSubject<boolean>(false);
  public multiClubs = false;
  public nothingHere = "There's nothing here at the moment";
  public isAuthenticated = new BehaviorSubject<boolean | null>(null);
  public isAuthenticated$ = this.isAuthenticated.asObservable();
  public token = '';
  public requestURL: string = '';
  public isNative = Capacitor.isNativePlatform();
  public isApp = Capacitor.isNativePlatform();
  public appPlatform: string = '';
  public appVersion: string = '';
  public appBuild: string = '';
  public deviceOS: string = '';
  public appType: string = '';
  public notificationData: any[] = [];
  public downloadProgress = 0;
  private errorPopup: boolean = true;
  private postingError = false;
  private inNonLoaders = false;
  private readTimeout = CONFIG.READ_TIMEOUT;
  private connectTimeout = CONFIG.CONNECT_TIMEOUT;
  public jwttoken: string = '';
  public club_id: string = '';
  public user_id: string = '';
  public cached: boolean = true;
  public clubLogoChanged = new BehaviorSubject<boolean>(false);
  public preferWheel = false;
  public logging_out = false; // Add this property

  public mapsReady: boolean = false;
  public mapsReadyCatCalled: boolean = false;
  public mapsReadyClubActivities: boolean = false;

  public streetHarassment: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([false]);
  public showInfoModal: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public isSwitchingClub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Swiper configs
  public swiperConfig = new Swiper('.swiper', {
    direction: 'horizontal',
    loop: true,
    zoom: { maxRatio: 3, minRatio: 1 },
    slidesPerView: 1,
    spaceBetween: 0,
    centeredSlides: true,
    modules: [],
  });

  public swiperStaticConfig = new Swiper('.swiper', {
    direction: 'horizontal',
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    centeredSlides: true,
    noSwiping: true,
    noSwipingClass: 'swiper-no-swiping',
    modules: [],
  });

  private readonly errorURL = 'ionic_postError';
  private readonly logoutURL = 'ionic_logout';
  private readonly sendNotificationsURL = 'ionic_sendNotifications';

  constructor(
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private storage: StorageService,
    private userService: UserService,
    private router: Router,
    private navController: NavController,
  ) {
    this.checkAuth();
  }

  async checkAuth(): Promise<boolean> {
    let isAuthed = false;

    try {

      // Get token from storage
      const token = await this.storage.getString('jwttoken');

      if (token?.value) {
        this.jwttoken = token.value; // Set GlobalService property
        
        // Also ensure UserService has the token
        this.userService.userDetails.jwttoken = token.value;
        
        isAuthed = true;
        await this.getAppDetails();
      } else {
        this.jwttoken = '';
        this.userService.userDetails.jwttoken = '';
        isAuthed = false;
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      this.jwttoken = '';
      this.userService.userDetails.jwttoken = '';
      isAuthed = false;
    }
    
    this.isAuthenticated.next(isAuthed);
    return isAuthed;

  }

  // --- Utility Methods ---

  generateUniqueId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private shouldThrottleRequest(reqKey: string): boolean {
    const now = Date.now();
    const lastRequest = this.recentRequests.get(reqKey);
    
    if (lastRequest && now - lastRequest < CONFIG.THROTTLE_MS) {
      return true;
    }
    
    this.recentRequests.set(reqKey, now);
    return false;
  }

  private isNonLoaderEndpoint(url: string): boolean {
    return NON_LOADER_ENDPOINTS.some(endpoint => url.includes(endpoint));
  }

  // --- UI Helpers ---

  async showAlert(message: string): Promise<void> {
    await Dialog.alert({ title: 'Stop', message });
  }

  async presentAlert(header: string, message: string, buttons: any[]): Promise<void> {
    const alert = await this.alertController.create({
      cssClass: 'buttonCss',
      header,
      message,
      buttons
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  async presentToast(
    message: string, 
    length: number, 
    position: 'top' | 'middle' | 'bottom' | string = 'bottom'
  ): Promise<void> {
    // Validate position parameter
    const validPositions: ('top' | 'middle' | 'bottom')[] = ['top', 'middle', 'bottom'];
    const safePosition = validPositions.includes(position as any) 
      ? (position as 'top' | 'middle' | 'bottom') 
      : 'bottom';

    const toast = await this.toastCtrl.create({
      message,
      duration: length,
      position: safePosition,
      animated: true
    });
    await toast.present();
  }

  // --- App Details and Device Info ---

  async getAppDetails(): Promise<void> {
    const currentRoute = this.router.url;
    
    if (currentRoute == '/login' || currentRoute == '/registration') {
      return;
    }
    
    try {
      // First try to get token from current service state
      if (!this.jwttoken) {
        // If not in service, try to get from storage
        const tokenData = await this.storage.getString('jwttoken');
        if (tokenData?.value) {
          this.jwttoken = tokenData.value;
          this.userService.userDetails.jwttoken = tokenData.value;
          console.log('✅ Retrieved jwttoken from storage');
        }
      }

      // Get club details from storage
      const clubDataWrapper = await this.storage.getObject('clubDetails');
      const clubData = clubDataWrapper?.value;
      
      if (clubData?.jwttoken && !this.jwttoken) {
        this.jwttoken = clubData.jwttoken;
        this.userService.userDetails.jwttoken = clubData.jwttoken;
        console.log('✅ Retrieved jwttoken from clubDetails');
      }

      // Get user ID
      if (!this.user_id) {
        this.user_id = this.userService.getUserId() || '';
        
        // Try to get from storage if still empty
        if (!this.user_id) {
          const userIdData = await this.storage.getString('user_id');
          if (userIdData?.value) {
            this.user_id = userIdData.value;
          }
        }
      }
      
      // Subscribe to clubID$ and assign the emitted value to this.club_id
      this.userService.clubID$.pipe(
        takeUntil(this.destroy$)
      ).subscribe((id: number | null) => {
        this.club_id = id != null ? id.toString() : '';
      });
      
      console.log('📊 App details status - JWT:', !!this.jwttoken, 'UserID:', !!this.user_id, 'ClubID:', !!this.club_id);
      
    } catch (error) {
      console.error('❌ Error getting app details:', error);
    }
  }

  async getDeviceDetails(): Promise<void> {
    try {
      if (!this.appPlatform || !this.deviceOS) {
        const deviceInfo = await Device.getInfo();
        this.appPlatform = deviceInfo.platform;
        this.deviceOS = deviceInfo.operatingSystem;
        this.deviceInfo = {
          platform: deviceInfo.platform,
          model: deviceInfo.model,
          operatingSystem: deviceInfo.operatingSystem
        };
      }

      if (this.isApp) {
        if (!this.appVersion || !this.appBuild || !this.appType) {
          const appInfo = await App.getInfo();
          this.appVersion = appInfo.version;
          this.appBuild = appInfo.build;
          this.appType = appInfo.name;
        }
      } else {
        if (!this.appVersion || !this.appBuild) {
          this.appVersion = version;
          this.appBuild = build;
        }
      }
    } catch (error) {
      console.error('Error getting device details:', error);
    }
  }

  protected async generateUrl(url: string): Promise<string> {
    return this.apiURL + url;
  }

  // --- HTTP Requests ---

  public async loginRequest(
  method: 'get' | 'post', 
  loader: boolean, 
  url: string, 
  body?: Record<string, any>
): Promise<Observable<any>> {
  const fullUrl = await this.generateUrl(url);
  this.requestURL = fullUrl;

  const reqHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  const options: RequestOptions = {
    url: fullUrl,
    headers: reqHeaders,
    data: body,
    readTimeout: this.readTimeout,
    connectTimeout: this.connectTimeout
  };

  return from(CapacitorHttp.request({ ...options, method })).pipe(
    takeUntil(this.destroy$),
    switchMap(async (response) => {
      if (response.status == 200) {
        const data = response.data;
        
        // Store JWT token if present in response
        if (data && data.jwttoken) {
          console.log('✅ JWT token received in login response:', data.jwttoken);
          
          // Store token in storage
          await this.storage.setString('jwttoken', data.jwttoken);
          
          // Set token in GlobalService
          this.jwttoken = data.jwttoken;
          
          // Set token in UserService
          this.userService.userDetails.jwttoken = data.jwttoken;
          
          console.log('✅ JWT token stored successfully');
        }
        
        return data;
      } else {
        throw new Error(this.handleKnownError('loginRequest', [response]));
      }
    }),
    catchError((error: any) => {
      console.error('❌ Error in loginRequest:', error);
      this.handleError('loginRequest', [error]);
      return throwError(() => error);
    }),
  );
}

  public async request<T = any>(
    method: 'get' | 'post', 
    loader: boolean, 
    url: string, 
    body?: Record<string, any>
  ): Promise<Observable<T>> {
    const fullUrl = await this.generateUrl(url);
    this.requestURL = fullUrl;

    // Throttle duplicate requests
    const reqKey = `${method}:${fullUrl}:${JSON.stringify(body || {})}`;
    if (this.shouldThrottleRequest(reqKey)) {
     // console.log('Request throttled:', reqKey);
      return of(null as T);
    }

    // Ensure we have necessary details
    await this.ensureAuthDetails();

    // Check if we have a valid token before making the request
    if (!this.jwttoken) {
      console.warn('No JWT token available for request:', fullUrl);
      // Try to get token from storage one more time
      const tokenData = await this.storage.getString('jwttoken');
      if (tokenData?.value) {
        this.jwttoken = tokenData.value;
      } else {
        // If still no token, this might be a login/logout request, so continue
        console.warn('No token found in storage either');
      }
    }

    const reqHeaders = {
      'Authorization': this.jwttoken ? 'bearer ' + this.jwttoken : '',
      'User-Id': this.user_id || '',
      'Club-Id': this.club_id || '',
      'Appversion': this.appVersion || '',
      'Build': this.appBuild || '',
      'Cmcversion': this.cMCVersion || '',
      'Platform': this.appPlatform || '',
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Remove empty Authorization header if no token
    if (!this.jwttoken) {
      delete reqHeaders['Authorization'];
    }

    // Process request body
    let requestBody: Record<string, string> = {};
    if (body) {
      Object.entries(body).forEach(([key, value]) => {
        if (Number.isInteger(value)) {
          requestBody[key] = value.toString();
        } else if (!value) {
          requestBody[key] = "";
        } else if (Array.isArray(value)) {
          requestBody[key] = value.join(',');
        } else {
          requestBody[key] = value.toString();
        }
      });
    }

    // Build options based on method
    let options: RequestOptions;
    if (method == 'get') {
      options = {
        url: fullUrl,
        headers: reqHeaders,
        params: requestBody,
        readTimeout: this.readTimeout,
        connectTimeout: this.connectTimeout
      };
    } else {
      options = {
        url: fullUrl,
        headers: reqHeaders,
        data: requestBody,
        readTimeout: this.readTimeout,
        connectTimeout: this.connectTimeout
      };
    }

    this.inNonLoaders = this.isNonLoaderEndpoint(this.requestURL);
    


    return from(CapacitorHttp.request({ ...options, method })).pipe(
      takeUntil(this.destroy$),
      retry({
        count: CONFIG.RETRY_COUNT,
        delay: (error, retryCount) => {
          console.log('Retry attempt:', retryCount, 'Error:', error);
          console.log('Request URL:', this.requestURL);
          return error.status == 403 ? of(null).pipe(delay(CONFIG.RETRY_DELAY)) : of(null);
        },
        resetOnSuccess: true
      }),
      switchMap((response: { status: number; data: T }) => {
       // console.log('Response status:', response.status);
       // console.log('Response data:', response.data);
        
        if (response.status == 200) {
          // Check for maintenance flag in successful response
          if (response.data && typeof response.data == 'object' && 
              (response.data as any).maintenance == true) {
            console.log('🔧 Maintenance mode detected in success response');
            return from(this.handleMaintenanceMode(response.data)).pipe(
              switchMap(() => EMPTY) // Return empty to stop processing
            );
          }
          return of(response.data);
        } else if (response.status == 503) {
          // Handle maintenance mode specifically
          console.log('🔧 Server in maintenance mode (503)');
          console.log('🔧 Maintenance data:', response.data);
          
          return from(this.handleMaintenanceMode(response.data)).pipe(
            switchMap(() => EMPTY) // Return empty instead of data to stop processing
          );
        } else {
          this.handleKnownError('request', [response]);
          console.log('Error in request:', response);
          return EMPTY;
        }
      }),
      catchError((error: any) => {
        console.log('🚨 Caught error:', error);
        
        // Check if the error has a 503 status
        if (error.status == 503) {
          console.log('🔧 Server in maintenance mode (503) - from catch');
          
          // The error data might be nested differently
          let maintenanceData = error.data || error.error || error;
          
          // If it's a string, try to parse it
          if (typeof maintenanceData == 'string') {
            try {
              maintenanceData = JSON.parse(maintenanceData);
            } catch (e) {
              // Keep as string if parsing fails
            }
          }
          
          return from(this.handleMaintenanceMode(maintenanceData)).pipe(
            switchMap(() => EMPTY)
          );
        }
        
        this.handleError('request', [error]);
        console.log('Error in request:', error);
        return EMPTY;
      }),
      finalize(() => {
      })
    );
  }

  // Add a new method to ensure we have all auth details
  private async ensureAuthDetails(): Promise<void> {
    // Get app details if missing
    if (!this.jwttoken || !this.user_id || !this.club_id) {
      await this.getAppDetails();
    }
    
    // Get device details if missing
    if (!this.appPlatform || !this.appVersion || !this.appBuild || !this.appType || !this.deviceOS) {
      await this.getDeviceDetails();
    }

    // If still no token, try to get it directly from storage
    if (!this.jwttoken) {
      try {
        const tokenData = await this.storage.getString('jwttoken');
        if (tokenData?.value) {
          this.jwttoken = tokenData.value;
          console.log('Retrieved token from storage:', this.jwttoken);
        }
      } catch (error) {
        console.error('Error getting token from storage:', error);
      }
    }
  }

  // --- Error Handling ---

  private handleKnownError<T>(operation = 'operation', result?: ErrorData[]): string {
    if (!result || !result[0]) {
      return 'Unknown error occurred';
    }

    const errorData = result[0];
    const status = errorData.status;
    
    // Skip error handling for maintenance mode
    if (status == 503) {
      console.log('🔧 Skipping error handling for maintenance mode (503)');
      return 'Maintenance mode';
    }

    let message = errorData.data?.message || errorData.message || 'Unknown error';

    // Handle specific token-related errors
    if (message.toLowerCase().includes('token')) {
      if (message.toLowerCase().includes('not found') || 
          message.toLowerCase().includes('missing') ||
          message.toLowerCase().includes('invalid')) {
        console.error('Token error detected, may need to re-authenticate');
        // Don't show generic token error to user
        return message;
      }
    }

    // Normalize common error messages
    const errorMessages = [
      'Missing security',
      'Unable to resolve host',
      'Wrong number',
      'You provided',
      'failed to connect to app.connectmyclub.co.uk'
    ];

    if (errorMessages.some(msg => message.includes(msg))) {
      message = 'Failed to connect to server';
    }

    // Don't show token errors to users via toast
    if (!message.toLowerCase().includes('token')) {
      this.presentToast(message, CONFIG.TOAST_DURATION, 'bottom');
    }
    
    if (status !== 418 && status !== 503) { // Also skip 503 here
      this.sendError(message, status);
    }
    
    return message;
  }

  private handleError<T>(operation = 'operation', result?: ErrorData[]): Observable<T> {
    if (!result || !result[0]) {
      return of(null as T);
    }

    const errorData = result[0];
    const status = (errorData as any).status;
    
    // Fix: Better code extraction - check multiple locations for the code
    // Your middleware sends: { message: "...", code: 503 } with status 503
    let code = errorData.code || (errorData as any).data?.code || status || 'unknown';

    // Derive a message string from available fields on the error object
    // Your middleware sends the message in the top level, not nested in data
    let message: string = '';
    
    if (errorData.data && typeof errorData.data == 'object') {
      // Standard API response structure
      message = errorData.data.message || errorData.message || (errorData as any).error || '';
    } else if (typeof errorData.data == 'string') {
      // Sometimes the entire response is in data as a string
      try {
        const parsed = JSON.parse(errorData.data);
        message = parsed.message || errorData.data;
        code = parsed.code || code;
      } catch {
        message = errorData.data;
      }
    } else {
      // Direct message field (your middleware case)
      message = errorData.message || (errorData as any).error || '';
    }
    
    // Skip error handling for maintenance mode
    if (status == 503 || code == 503) {
      console.log('🔧 Skipping error handling for maintenance mode (503)');
      return of(result as T);
    }

    // Handle common network errors
    const networkErrors: Record<string, string> = {
      "SocketTimeoutException": "Timed Out",
      "TimeoutError": "Request timeout",
      "Request timeout": "Request timeout",
      "The request timed out.": "Request timed out",
      "The network connection was lost.": "Network Error",
      "The Internet connection appears to be offline.": "The Internet connection appears to be offline.",
      "Failed to fetch": "Server error",
      "Network Error": "Network Error",
      "Request aborted": "Request aborted"
    };

    if (message && networkErrors[message]) {
      message = networkErrors[message];
    }

    console.error(`Error in ${operation}:`, {
      message,
      code,
      status,
      originalError: errorData,
      middlewareData: errorData.data // Add this for debugging
    });

    // Skip certain error types
    const skipErrors = [
      'Wrong number of',
      'You provided',
      'timeout',
      'Token',
      'Please try again',
      'Failed to connect to server',
      'Unable to resolve host',
      'failed to connect to app.connectmyclub.co.uk',
      'Missing security'
    ];

    if (message && skipErrors.some(skip => message.includes(skip))) {
      return of(result as T);
    }

    if (!message) {
      message = "An unknown error has occurred";
    }

    this.presentToast(message, CONFIG.TOAST_DURATION, 'bottom');
    
    // Only send error if we have a valid code (not undefined) and it's not maintenance
    if (code && code !== 'unknown' && code !== 503 && status !== 503) {
      this.sendError(message, code);
    }
    
    return of(result as T);
  }

  async sendError(error: string, status: any): Promise<void> {
    if (this.postingError || this.requestURL.includes("ionic_postError")) {
      return;
    }

    // Don't send error reports for certain conditions
    if (!status || status == 'unknown' || status == 503) {
      console.log('Skipping error report for status:', status);
      return;
    }

    this.postingError = true;

    try {
      const info = await Device.getInfo();
      const deviceModel = info.model;
      
      const dataString = {
        'section': this.requestURL,
        'platform': this.appPlatform,
        'device': deviceModel,
        'error': error,
        'version': this.appVersion,
        'build': this.appBuild,
        'status': String(status) // Ensure status is always a string
      };

      const sub = (await this.request('post', false, this.errorURL, dataString))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (res: any) => {
          this.postingError = false;
          if (res?.jwttoken) {
            await this.storage.setString('jwttoken', res.jwttoken);
          }
        });

      this.subscriptions.add(sub);
    } catch (error) {
      console.error('Error sending error report:', error);
      this.postingError = false;
    }
  }

  // --- API Methods ---

  async postPageViewed(route: string): Promise<void> {
    const dataString = { 'route': route };
    
    const sub = (await this.request('post', false, 'ionic_postPageViews', dataString))
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (res: any) => {
       // console.log('postPageViewed', res);
      });
      
    this.subscriptions.add(sub);
  }

  async usageStats(getting: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Badge.clear();
    }

    try {
      const data = await this.storage.getString('identifier');
      const identifier = data?.value;
      
      if (!identifier) {
        console.warn('No device identifier found');
        return;
      }

      const dataString = { 'identifier': identifier };

      //delay to allow app to settle
      await this.delay(3000);
    //  console.log('Sending usageStats with identifier:', identifier);
      
      const sub = (await this.request('post', false, 'ionic_postUsageStats', dataString))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (res: any) => {
          if (!res) {
            console.log('usageStats: No response received');
            return;
          }

        //console.log('usageStats response:', res);
          
          // Clear existing notifications first
          this.userService.unreadNotifications.next([]);
          
          // Update with new data
          if (res.unread) {
            this.userService.unreadNotifications.next(res.unread);
          }
          
          if (res.jwttoken) {
            await this.storage.setString('jwttoken', res.jwttoken);
          }

          if (res.streetHarassment) {
            this.streetHarassment.next(res.streetHarassment);
          }

          if (res.logout) {
            this.logout();
          }
        /*  if( !res.show_membership_form){
            this.userService.showMembershipForm.next(false);
          }*/
          
          /*if (!res.approved) {
            this.presentToast('Your club has blocked your app access. Please contact the club for more information.', 5000);
          }*/
         
        });
        
      this.subscriptions.add(sub);
    } catch (error) {
      console.error('Error in usageStats:', error);
    }
  }

  async sendNotifications(section: string, id: any): Promise<void> {
    const dataString = { 'section': section, 'id': id };
    
    const sub = (await this.request('post', false, this.sendNotificationsURL, dataString))
      .pipe(takeUntil(this.destroy$))
      .subscribe();
      
    this.subscriptions.add(sub);
  }

  async getTabValues(): Promise<void> {
    const sub = (await this.request('get', false, 'ionic_getAppTabValues', null))
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (res: any) => {
        this.tabValues.next(res);
      });
      
    this.subscriptions.add(sub);
  }



 

  // --- Auth ---

  async logout(): Promise<void> {
    const currentRoute = this.router.url;
    console.log('Current route:', currentRoute);

    if (currentRoute == '/login' || currentRoute == '/registration') {
      return;
    }

    this.isAuthenticated.next(false);
    
    try {
      const info = await Device.getInfo();
      const device = await Device.getId();
      
      const dataString = { 
        'platform': info.platform, 
        'uuid': device.identifier 
      };
      
      this.logging_out = true;
      
      const sub = (await this.request('post', false, this.logoutURL, dataString))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async () => {
          await this.storage.clear();

          // Reset UserService state properly
          this.userService.resetUserState();
          
          // Reset GlobalService properties
          this.jwttoken = '';
          this.user_id = '';
          this.club_id = '';

          // Reset theme colors
          const theme = {
            primary: CONFIG.DEFAULT_PRIMARY_COLOR,
            primaryRgb: CONFIG.DEFAULT_PRIMARY_RGB,
            secondary: CONFIG.DEFAULT_SECONDARY_COLOR,
            secondaryRgb: CONFIG.DEFAULT_SECONDARY_RGB,
            tertiary: CONFIG.DEFAULT_PRIMARY_COLOR,
            tertiaryRgb: CONFIG.DEFAULT_PRIMARY_RGB
          };

          this.userService.setThemeColors(theme);

          // clear local storage
          await this.storage.clear();

          // remove .dark from body
          document.body.classList.remove('dark');

          this.router.navigate([''], { replaceUrl: true });
        });
        
      this.subscriptions.add(sub);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  async switchLogout(): Promise<void> {
    try {
      const info = await Device.getInfo();
      const device = await Device.getId();
      
      const dataString = { 
        'platform': info.platform, 
        'uuid': device.identifier 
      };
      
      const sub = (await this.request('post', false, this.logoutURL, dataString))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async () => {
          // Reset CSS properties
          const defaultColors = {
            '--ion-color-primary': CONFIG.DEFAULT_PRIMARY_COLOR,
            '--ion-color-base-rgb': CONFIG.DEFAULT_PRIMARY_RGB,
            '--ion-color-primary-shade': CONFIG.DEFAULT_PRIMARY_RGB,
            '--ion-color-primary-tint': CONFIG.DEFAULT_PRIMARY_RGB,
            '--ion-color-primary-contrast': CONFIG.DEFAULT_SECONDARY_COLOR,
            '--ion-color-contrast-rgb': CONFIG.DEFAULT_SECONDARY_RGB,
            '--ion-color-tertiary': CONFIG.DEFAULT_PRIMARY_COLOR,
            '--ion-color-tertiary-rgb': CONFIG.DEFAULT_PRIMARY_RGB,
            '--ion-color-tertiary-shade': CONFIG.DEFAULT_PRIMARY_RGB,
            '--ion-color-tertiary-tint': CONFIG.DEFAULT_PRIMARY_RGB
          };

          Object.entries(defaultColors).forEach(([property, value]) => {
            document.documentElement.style.setProperty(property, value);
          });
        });
        
      this.subscriptions.add(sub);
    } catch (error) {
      console.error('Error during switch logout:', error);
    }
  }

  // --- Status Bar ---
/*
  async setStatusBar(bg: string, text: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      // Ensure bg is a valid hex string
      const validHex = /^#[0-9A-Fa-f]{6}$/.test(bg) ? bg : CONFIG.DEFAULT_PRIMARY_COLOR;
      
      await StatusBar.setBackgroundColor({ color: validHex });
      await StatusBar.setStyle({ 
        style: text == 'DARK' ? Style.Dark : Style.Light 
      });
    } catch (error) {
      console.error('Error setting status bar:', error);
    }
  }*/

  // --- Push Notifications ---

  async registerToken(pushToken: string): Promise<void> {
    try {
      const appUsedClub = 'appused_' + this.club_id;
      const data = await this.storage.getString(appUsedClub);
      const reset = data?.value !== 'true';
      
      const info = await Device.getInfo();
      const device = await Device.getId();
      
      const dataString = { 
        'platform': info.platform, 
        'uuid': device.identifier, 
        'token': pushToken, 
        'reset': reset 
      };
      
      setTimeout(async () => {
        const sub = (await this.request('post', false, 'ionic_postPushToken', dataString))
          .pipe(takeUntil(this.destroy$))
          .subscribe(async (res: any) => {
            if (res) {
              await this.storage.setString(appUsedClub, 'true');
            }
          });
          
        this.subscriptions.add(sub);
      }, 1000);
    } catch (error) {
      console.error('Error registering token:', error);
    }
  }

  public async checkWebPushPermissions(): Promise<void> {
    try {
      const permission = await FirebaseMessaging.checkPermissions();
      if (permission.receive !== 'granted') {
        await FirebaseMessaging.requestPermissions();
        this.pushPermission.next(false);
      } else {
        await this.getPushToken();
        this.pushPermission.next(true);
      }
    } catch (error) {
      console.error('Error checking push permissions:', error);
    }
  }

  public async getPushToken(): Promise<void> {
    try {
      const options: GetTokenOptions = {
        vapidKey: environment.firebase.vapidKey,
      };
      
      if (Capacitor.getPlatform() == "web") {
        options.serviceWorkerRegistration =
          await navigator.serviceWorker.register("firebase-messaging-sw.js");
      }
      
      const { token } = await FirebaseMessaging.getToken(options);
      await this.storage.setString('pushtoken', token);
      this.registerToken(token);
    } catch (error) {
      console.error('Error getting push token:', error);
    }
  }

  // --- File Operations ---

  async downloadGPX(url: string, type: 'gpx' | 'tcx'): Promise<void> {
    try {
      const route = url.match(/\d+/g)?.[0] || 'unknown';
      const timestamp = Date.now();
      const filename = type == 'gpx' 
        ? `route_${route}_${timestamp}.gpx`
        : `plan${route}_${timestamp}.tcx`;
      const mimeType = type == 'gpx' 
        ? 'application/gpx+xml' 
        : 'application/gpx+tcx';

      const status = await Filesystem.checkPermissions();
      if (status.publicStorage == 'granted') {
        const res = await Filesystem.downloadFile({
          url,
          path: filename,
          directory: Directory.Documents,
          progress: true,
          recursive: true
        });
        
        if (res.path) {
          await this.openGpx(res.path, mimeType);
        }
      } else {
        await Filesystem.requestPermissions();
      }
    } catch (error) {
      console.error('Error downloading GPX:', error);
    }
  }

  private async openGpx(filePath: string, mimeType: string): Promise<void> {
    try {
      const openDefault = Capacitor.getPlatform() !== 'ios';
      const options = { filePath, openWithDefault: openDefault };
      
      await FileOpener.open(options);
      console.log('File is opened');
    } catch (error) {
      console.error('Error opening file', error);
    }
  }

  async downloadPDF(url: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.openPdf(url);
      return;
    }

    try {
      const shareUrl = url.replace(this.apiURL, '');
      const sub = (await this.request('get', true, shareUrl, null))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (res: any) => {
          if (!res) return;

          try {
            const result = await Filesystem.writeFile({
              path: res.new_title,
              data: res.filedata,
              directory: Directory.Documents,
              recursive: true
            });

            const options = { 
              filePath: result.uri, 
              contentType: res.mime_type, 
              openWithDefault: true 
            };
            
            await FileOpener.open(options);
            console.log('File is opened');
          } catch (error) {
            console.error('Error handling PDF:', error);
            this.openPdf(url);
          }
        });
        
      this.subscriptions.add(sub);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      this.openPdf(url);
    }
  }

  openPdf(file: string): void {
    Browser.open({ url: file });
  }


  // --- Calendar ---
async addEventToCalendar(title: string, location: string, startDate: string): Promise<void> {
  console.log('Adding event to calendar:', { title, location, startDate });
  
  try {
    if (Capacitor.isNativePlatform()) {
      // Check calendar permissions first
      let permissionStatus: any = await CapacitorCalendar.checkAllPermissions();

      console.log('Initial calendar permission status:', permissionStatus);

      let readCalendar;
      let writeCalendar;

      if(Capacitor.getPlatform() == 'ios'){
       readCalendar = permissionStatus.result.readCalendar == 'granted';
       writeCalendar = permissionStatus.result.writeCalendar == 'granted';
      } else if(Capacitor.getPlatform() == 'android'){
        readCalendar = permissionStatus.readCalendar == 'granted';
        writeCalendar = permissionStatus.writeCalendar == 'granted';
      }

      console.log('Read Calendar Permission:', readCalendar);
      console.log('Write Calendar Permission:', writeCalendar);
      
      if (!readCalendar || !writeCalendar) {
        this.presentToast('Calendar permissions are required to add events', CONFIG.TOAST_DURATION, 'bottom');
        permissionStatus = await CapacitorCalendar.requestFullCalendarAccess();
        console.log('Updated calendar permission status:', permissionStatus);
        return;
      }

      console.log('Original Start Date:', startDate, 'Type:', typeof startDate);

      // Fix: Properly parse the startDate
      let startMs: number;
      
      if (typeof startDate === 'number') {
        // Already a timestamp
        startMs = startDate;
      } else if (typeof startDate === 'string') {
        // Check if it's a numeric string (timestamp)
        const numericValue = Number(startDate);
        if (!isNaN(numericValue) && numericValue > 1000000000) { // Reasonable timestamp check
          // If it's a timestamp as string, convert to number
          startMs = numericValue;
          // If it looks like seconds instead of milliseconds, convert
          if (startMs < 10000000000) { // Less than year 2286 in seconds
            startMs = startMs * 1000; // Convert seconds to milliseconds
          }
        } else {
          // Parse as date string
          const parsedDate = new Date(startDate);
          if (isNaN(parsedDate.getTime())) {
            console.error('Invalid date format:', startDate);
            startMs = Date.now(); // Fallback to current time
          } else {
            startMs = parsedDate.getTime();
          }
        }
      } else {
        console.error('Unexpected startDate type:', typeof startDate, startDate);
        startMs = Date.now(); // Fallback to current time
      }

      console.log('Processed Start Time (ms):', startMs);
      console.log('Start Date Object:', new Date(startMs));

      const endMs = startMs + 60 * 60 * 1000; // default to 1 hour later

      const eventOptions = {
        title: title,
        location: location || '',
        startDate: startMs,
        endDate: endMs,
        isAllDay: false,
        alertOffsetInMinutes: [15], // 15 minutes before event
        url: '',
        notes: `Added from ${this.appName}`
      };

      console.log('Event Options:', eventOptions);
      console.log('Event Start Date Check:', new Date(eventOptions.startDate));

      let create = await CapacitorCalendar.createEventWithPrompt(eventOptions);

      if(create.id){
        console.log('Event created with ID:', create.id);
        /* this.presentToast('Event created, please allow time for the calendar to sync', CONFIG.SUCCESS_TOAST_DURATION, 'bottom');*/
      } else {
        console.error('Failed to create event in calendar');
       // this.presentToast('Failed to add event to calendar', CONFIG.TOAST_DURATION, 'bottom');
      }
      
    } else {
      // Web fallback - create ICS file
      const dataString = { title, location, startDate };

      const sub = (await this.request('post', false, 'ionic_postCreateICS', dataString))
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (res: any) => {
          console.log('ICS response:', res);
          if (res?.url) {
            this.openPdf(res.url);
            this.presentToast('Calendar file downloaded', CONFIG.SUCCESS_TOAST_DURATION, 'bottom');
          }
        });
        
      this.subscriptions.add(sub);
    }
  } catch (error) {
    console.error('Error adding event to calendar:', error);
    this.presentToast('Failed to add event to calendar', CONFIG.TOAST_DURATION, 'bottom');
    
    // Fallback to web method if native fails
    if (Capacitor.isNativePlatform()) {
      try {
        const dataString = { title, location, startDate };
        const sub = (await this.request('post', false, 'ionic_postCreateICS', dataString))
          .pipe(takeUntil(this.destroy$))
          .subscribe(async (res: any) => {
            console.log('Fallback ICS response:', res);
            if (res?.url) {
              this.openPdf(res.url);
            }
          });
          
        this.subscriptions.add(sub);
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
      }
    }
  }
}
  


  // --- Image Saving ---

  async saveImageToDevice(imageUrl: string, title: string): Promise<void> {
    try {
      this.presentToast('Saving image...', CONFIG.LOADING_TOAST_DURATION, 'bottom');

      if (Capacitor.isNativePlatform()) {
        await this.saveImageNative(imageUrl, title);
      } else {
        await this.saveImageWeb(imageUrl, title);
      }

      this.presentToast('Image saved successfully', CONFIG.SUCCESS_TOAST_DURATION, 'bottom');
    } catch (error) {
      console.error('Error saving image:', error);
      this.presentToast('Failed to save image', CONFIG.TOAST_DURATION, 'bottom');
      throw error;
    }
  }

  private async saveImageNative(imageUrl: string, title: string): Promise<void> {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const base64data = reader.result as string;
            const base64String = base64data.split(',')[1];
            const fileName = `${title || 'image'}_${Date.now()}.jpg`;
            
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64String,
              directory: Directory.Documents,
              recursive: true
            });
            
            await Share.share({
              title: title || 'Image',
              url: result.uri,
              dialogTitle: 'Save Image'
            });
            
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw error;
    }
  }

  private async saveImageWeb(imageUrl: string, title: string): Promise<void> {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'image'}_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }

  async saveImageToDeviceWithProgress(
    imageUrl: string, 
    title: string, 
    progressCallback?: (progress: number) => void
  ): Promise<void> {
    try {
      this.presentToast('Saving image...', CONFIG.LOADING_TOAST_DURATION, 'bottom');

      if (Capacitor.isNativePlatform()) {
        await this.saveImageNativeWithProgress(imageUrl, title, progressCallback);
      } else {
        await this.saveImageWebWithProgress(imageUrl, title, progressCallback);
      }

      this.presentToast('Image saved successfully', CONFIG.SUCCESS_TOAST_DURATION, 'bottom');
    } catch (error) {
      console.error('Error saving image with progress:', error);
      this.presentToast('Failed to save image', CONFIG.TOAST_DURATION, 'bottom');
      throw error;
    }
  }

  private async saveImageNativeWithProgress(
    imageUrl: string, 
    title: string, 
    progressCallback?: (progress: number) => void
  ): Promise<void> {
    try {
      // Report initial progress
      progressCallback?.(0);

      // Fetch with progress tracking
      const response = await this.fetchWithProgress(imageUrl, progressCallback);
      const blob = await response.blob();
      
      // Report 90% when blob is ready
      progressCallback?.(90);
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const base64data = reader.result as string;
            const base64String = base64data.split(',')[1];
            const fileName = `${title || 'image'}_${Date.now()}.jpg`;
            
            const result = await Filesystem.writeFile({
              path: fileName,
              data: base64String,
              directory: Directory.Documents,
              recursive: true
            });
            
            // Report 95% when file is written
            progressCallback?.(95);
            
            await Share.share({
              title: title || 'Image',
              url: result.uri,
              dialogTitle: 'Save Image'
            });
            
            // Report 100% when complete
            progressCallback?.(100);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        
        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      throw error;
    }
  }

  private async saveImageWebWithProgress(
    imageUrl: string, 
    title: string, 
    progressCallback?: (progress: number) => void
  ): Promise<void> {
    try {
      // Report initial progress
      progressCallback?.(0);

      // Fetch with progress tracking
      const response = await this.fetchWithProgress(imageUrl, progressCallback);
      const blob = await response.blob();
      
      // Report 90% when blob is ready
      progressCallback?.(90);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'image'}_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Report 100% when complete
      progressCallback?.(100);
    } catch (error) {
      throw error;
    }
  }

  private async fetchWithProgress(
    url: string, 
    progressCallback?: (progress: number) => void
  ): Promise<Response> {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentLength = response.headers.get('content-length');
    if (!contentLength) {
      // If no content-length header, just return the response
      progressCallback?.(50); // Report 50% as we can't track actual progress
      return response;
    }

    const total = parseInt(contentLength, 10);
    let loaded = 0;

    const reader = response.body?.getReader();
    if (!reader) {
      progressCallback?.(50);
      return response;
    }

    const stream = new ReadableStream({
      start(controller) {
        function pump(): Promise<void> {
          return reader.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }

            loaded += value.length;
            const progress = Math.round((loaded / total) * 80); // Reserve 80% for download, 20% for processing
            progressCallback?.(progress);

            controller.enqueue(value);
            return pump();
          });
        }
        return pump();
      }
    });

    return new Response(stream, {
      headers: response.headers,
      status: response.status,
      statusText: response.statusText
    });
  }

  // --- Utility Methods ---

  async shareDetails(detail: string): Promise<void> {
    try {
      await Share.share({ text: detail });
    } catch (error) {
      console.error('Error sharing details:', error);
    }
  }

  updateClubName(value: string): void {
    this.clubName = value;
  }

  hasMultiClubs(value: boolean): void {
    this.multiClubs = value;
  }

  getToken(): string | null {
      return this.userService.getJwtToken();
  }

  ngOnDestroy(): void {
    this.maintenanceAlertShown = false;
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  // Add new method to handle maintenance mode
  private async handleMaintenanceMode(data: any): Promise<void> {
    console.log('🔧 Handling maintenance mode:', data);
    
    // Check if alert is already shown to prevent duplicates
    if (this.maintenanceAlertShown) {
      console.log('🔧 Maintenance alert already shown, skipping');
      return;
    }
    
    // Extract maintenance message from response data
    let maintenanceMessage = data.message;
    let maintenanceTitle = data.title || 'Maintenance Notice';
    
    console.log('🔧 Maintenance message extracted:', maintenanceMessage);

    // Show maintenance alert instead of toast
    await this.presentMaintenanceAlert(maintenanceTitle,maintenanceMessage);
  }

  // Update the presentMaintenanceAlert method to manage the flag
  private async presentMaintenanceAlert(title: string, message: string): Promise<void> {
    try {
      // Set flag to prevent duplicate alerts
      this.maintenanceAlertShown = true;
      
      const alert = await this.alertController.create({
        cssClass: 'maintenance-alert',
        header: title,
        message: message,
        buttons: [
          {
            text: 'OK',
            role: 'confirm',
            cssClass: 'alert-button-confirm',
            handler: () => {
              // Reset flag when user dismisses the alert
              this.resetMaintenanceAlert();
            }
          }
        ],
        backdropDismiss: false // Prevent dismissing by clicking outside
      });

      // Reset flag when alert is dismissed (backup)
      alert.onDidDismiss().then(() => {
        this.resetMaintenanceAlert();
      });

      await alert.present();
      
      console.log('🔧 Maintenance alert presented');
      
    } catch (error) {
      console.error('Error showing maintenance alert:', error);
      // Reset flag on error
      this.maintenanceAlertShown = false;
      // Fallback to toast if alert fails
      this.presentToast(message, CONFIG.TOAST_DURATION * 2, 'top'); // Longer duration for maintenance
    }
  }

  // Add method to reset the maintenance alert flag
  private resetMaintenanceAlert(): void {
    console.log('🔧 Resetting maintenance alert flag');
    // Add a small delay to prevent immediate re-triggering
    setTimeout(() => {
      this.maintenanceAlertShown = false;
    }, 1000); // 1 second delay
  }
}